import type { ReturnStepImageBox } from 'types/balder';
import { PROVIDE_LABEL_OPTIONS } from 'constants/exchanges';
import { UPS_FIND_STORE_URL } from 'constants/appConstants';
import ManCarryingPackage from 'images/zappos/man-carrying-package.svg';
import PrintingReturnLabel from 'images/zappos/printing-return-label.svg';
import RemoveLabels from 'images/zappos/RemoveLabels.svg';
import StackedBoxes from 'images/zappos/stacked-boxes.svg';
import DropoffBox from 'images/zappos/dropoff-box.svg';
import PackBox from 'images/zappos/pack-box.svg';
import PrintLabel from 'images/zappos/print-label.svg';
import SealBox from 'images/zappos/seal-box.svg';
import UPSPickup from 'images/zappos/ups-pickup.svg';
import PrintingReturnLabelUnbranded from 'images/6pm/PrintingReturnLabelUnbranded.png';
import ManCarryingPackageUnbranded from 'images/6pm/ManCarryingPackageUnbranded.png';
import StackedBoxesUnbranded from 'images/6pm/StackedBoxesUnbranded.png';
import Scan from 'images/zappos/lfbf_scan.svg';
import BringItem from 'images/zappos/bring_item.svg';

interface ReturnStep {
  heading: string;
  link: string;
  boxes: ReturnStepImageBox[];
  newBoxes?: ReturnStepImageBox[];
}

interface ReturnSteps {
  [key: string]: ReturnStep;
}

export const PRINT_OR_EMAIL_RETURN_STEPS: ReturnStep = {
  heading: 'How to Return Your Item(s)',
  link: 'our return policy',
  boxes: [
    {
      title: 'Print Your Label',
      img: PrintingReturnLabel,
      description: 'Ship your return back to us using any cardboard box or a Zappos box (remove pre-existing labels and markings).'
    },
    {
      title: 'Pack Your Box',
      img: ManCarryingPackage,
      description: "Place item(s) you're returning back in their original packaging and then put in the cardboard shipping box from step 1."
    },
    {
      title: 'Send it Back Through UPS',
      img: StackedBoxes,
      description: 'Seal box, attach return label, and take to an authorized UPS location.'
    }
  ],
  newBoxes: [
    {
      title: 'Print Your Label and Prep Your Return',
      img: PrintLabel,
      description:
        "Use your printer to print your free return label. Place item(s) you're returning in their original packaging. Be sure to include everything the item came with."
    },
    {
      title: 'Pack Your Box For Shipping & Seal It',
      img: SealBox,
      description:
        "Use any shipping box, remove pre-existing labels, add item(s) you're returning to the box, then seal the shipping box with packing tape."
    },
    {
      title: 'Attach Return Label & Drop Off at a UPS Store',
      img: DropoffBox,
      description: 'Attach your FREE return label to the shipping box, then take your return to an authorized UPS location.'
    }
  ]
};

export const ANY_CARRIER_RETURN: ReturnStep = {
  heading: 'How to Return Your Item(s)',
  link: 'our return policy',
  boxes: [
    {
      title: 'Print Your Label',
      img: PrintingReturnLabelUnbranded,
      description: "Place the item(s) you're returning back in their original packaging and put them in any cardboard box or a 6PM shipping box/bag."
    },
    {
      title: 'Pack Your Box',
      img: ManCarryingPackageUnbranded,
      description: 'Remove any pre-existing labels or markings from the shipping box and seal it with clear packing tape.'
    },
    {
      title: 'Send it Back',
      img: StackedBoxesUnbranded,
      description:
        'Attach the return label included in this email, and take the box to your nearest USPS, UPS or FedEx location. (You are responsible for return shipping fees.)'
    }
  ]
};

export const RETURN_STEPS: ReturnSteps = {
  [PROVIDE_LABEL_OPTIONS.LABELLESS]: {
    heading: 'How to Return Your Item(s)',
    link: 'our return policy',
    boxes: [
      {
        title: 'Pack Your Box',
        img: StackedBoxes,
        description:
          'Place item(s) you’re returning back in their original packaging and put them in any cardboard box or a Zappos shipping box/bag. Remove any pre-existing labels or markings from the return shipping box/bag.'
      },
      {
        title: 'Take to a Local UPS Store',
        img: ManCarryingPackage,
        description: `Seal box/bag and take to The UPS Store. <a href="${UPS_FIND_STORE_URL}">Find the Closest UPS Store</a>.`
      },
      {
        title: 'Show Your Return Code',
        img: PrintingReturnLabel,
        description: 'Show your return code to The UPS Store associate and they’ll print a FREE return label.'
      }
    ],
    newBoxes: [
      {
        title: 'Prep Your Return & Pack Your Box',
        img: PackBox,
        description:
          "Place item(s) you're returning back in their original packaging. Then, use any shipping box, remove pre-existing labels, and add the item(s) you're returning to the box."
      },
      {
        title: 'Seal Your Box For Shipping and Take to UPS',
        img: SealBox,
        description: 'Seal the shipping box completely, then take your return to an authorized UPS location.'
      },
      {
        title: 'Drop Off at a UPS Store & Scan Return Code',
        img: DropoffBox,
        description:
          "Have an associate scan your return code on your mobile device at an authorized UPS location. They'll print and attach a FREE label to your return. All done!"
      }
    ]
  },
  [PROVIDE_LABEL_OPTIONS.PICKUP]: {
    heading: 'How to Return Your Item(s)',
    link: 'our return policy',
    boxes: [
      {
        title: 'Pack Your Box',
        img: ManCarryingPackage,
        description:
          'Place item(s) you’re returning back in their original packaging and put them in any cardboard box or a Zappos shipping box/bag.'
      },
      {
        title: 'Remove Pre-existing Labels',
        img: RemoveLabels,
        description:
          'Remove any pre-existing labels or markings from the package and seal it using packing tape. UPS will add the shipping label for you!'
      },
      {
        title: 'Send it Back Through UPS',
        img: StackedBoxes,
        description:
          'UPS will attempt to pick up your return package the next business day. Pickups occur Monday - Friday from 8 AM to 8 PM your local time, excluding UPS’ observed holidays.'
      }
    ],
    newBoxes: [
      {
        title: 'Prep Your Return',
        img: PackBox,
        description: "Place item(s) you're returning back in their original packaging. Be sure to include everything the item came with."
      },
      {
        title: 'Pack Your Box For Shipping & Seal It',
        img: SealBox,
        description:
          "Use any shipping box, remove pre-existing labels, add item(s) you're returning to the box, then seal the shipping box with packing tape."
      },
      {
        title: 'Wait for UPS to Arrive',
        img: UPSPickup,
        description: 'UPS will pick up your return the next business day. UPS asks that you be at home for them to retrieve your package.'
      }
    ]
  },
  [PROVIDE_LABEL_OPTIONS.UPS_PACKAGELESS]: {
    heading: 'How to Return Your Item(s)',
    link: 'our return policy',
    boxes: [
      {
        title: 'Take to a Local UPS Store',
        img: BringItem,
        description: `Seal box/bag and take to The UPS Store. <a href="${UPS_FIND_STORE_URL}">Find the Closest UPS Store</a>.`
      },
      {
        title: 'Show Your Return Code',
        img: Scan,
        description: 'Show your return code to The UPS Store associate and they’ll print a FREE return label.'
      }
    ],
    newBoxes: [
      {
        title: 'Bring Your Items to the Store',
        img: BringItem,
        description:
          'Bring the item(s) in their original manufacturer’s packaging (i.e. shoe boxes or clothing bag) and disassembled if applicable to The UPS Store. Remember, no shipping box is required.'
      },
      {
        title: 'Scan Your Return Code',
        img: Scan,
        description:
          'Show the return code on your mobile device to The UPS Store associate, who will scan the code and then pack and ship your return for FREE.'
      }
    ]
  },
  [PROVIDE_LABEL_OPTIONS.WFM_PACKAGELESS]: {
    heading: 'How to Return Your Item(s)',
    link: 'our return policy',
    boxes: [
      {
        title: 'Take to a Local UPS Store',
        img: BringItem,
        description: `Seal box/bag and take to The UPS Store. <a href="${UPS_FIND_STORE_URL}">Find the Closest UPS Store</a>.`
      },
      {
        title: 'Show Your Return Code',
        img: Scan,
        description: 'Show your return code to The UPS Store associate and they’ll print a FREE return label.'
      }
    ],
    newBoxes: [
      {
        title: 'Bring Your Items to the Store',
        img: BringItem,
        description:
          'Bring the item(s) in their original manufacturer’s packaging (i.e. shoe boxes or clothing bag) and disassembled if applicable to a Whole Foods Market. Remember, no shipping box is required. '
      },
      {
        title: 'Scan Your Return Code',
        img: Scan,
        description:
          'Locate the Amazon Return Counter or Whole Foods Market Customer Service Desk inside the Whole Foods Market store and show the return code on your mobile device to a store associate, who will scan the code and then pack and ship your return for FREE.'
      }
    ]
  },
  [PROVIDE_LABEL_OPTIONS.PRINT]: PRINT_OR_EMAIL_RETURN_STEPS,
  [PROVIDE_LABEL_OPTIONS.EMAIL]: PRINT_OR_EMAIL_RETURN_STEPS,
  [PROVIDE_LABEL_OPTIONS.ANY_CARRIER]: ANY_CARRIER_RETURN
};
